import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Row, Col, Statistic, Typography, Tag, Space, Button, message } from 'antd';
import { BarChartOutlined, DollarOutlined, ShopOutlined, ReloadOutlined, FileExcelOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import DataTable, { type DataTableRef } from '../../components/Charts/DataTable';
import type { ColumnsType } from 'antd/es/table';
import { overdueApi, type StoreAggregateStatisticsResponse } from '../../services/statistics/overdue';
import { useShopMap, useMainBodyMap } from '../../services/store/organization.store';
import { mainBodyStoreIdsSingleSelect } from '../../components/Business/QueryFieldPresets';
import type { StoreStatistics } from '../../types/entity/overdue';
import Dashboard from '../../components/Templates/Dashboard';
import type { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';
import { getDefaultQuery, getKpiStatic } from './effect';
import { markShopSortFunc } from '../../utils/format.utils';

const { Title, Text } = Typography;

const OverdueStoreStatistics: React.FC = () => {
  const { shopList, shopMap } = useShopMap();
  const { mainBodyList } = useMainBodyMap();
  const tableRef = useRef<DataTableRef>(null);


  const fields: FieldConfig[] = useMemo(() => [
    {
      key: 'orderCreatedAt',
      label: '接单日期',
      type: 'date',
      operators: ['between'],
      format: 'YYYY-MM-DD',
      placeholder: ['开店日期', '至今']
    },
    mainBodyStoreIdsSingleSelect(mainBodyList, shopList),
  ], [shopList, mainBodyList]);

  const columns: ColumnsType<StoreStatistics> = [
    {
      title: '店铺',
      dataIndex: 'storeId',
      key: 'storeId',
      fixed: 'left',
      width: 120,
      sorter: markShopSortFunc(shopMap),
      sortOrder: 'ascend',
      render: (text: number) => (
        <Text>{ shopMap.get?.(Number(text))?.name }</Text>
      )
    },
    {
      title: '已租订单',
      dataIndex: 'totalRentOrderCount',
      key: 'totalRentOrderCount',
      align: 'center',
      width: 80,
    },
    {
      title: 'M1订单数',
      dataIndex: 'm1OrderCount',
      key: 'm1OrderCount',
      align: 'center',
      width: 80,
    },
    {
      title: '总逾期订单数',
      dataIndex: 'historicalOverdueOrderCount',
      key: 'historicalOverdueOrderCount',
      align: 'center',
      width: 110,
      render: (value: number) => <Text type={value > 0 ? 'warning' : 'success'}>{value}</Text>
    },
    {
      title: '逾期率-订单',
      dataIndex: 'orderOverdueRate',
      key: 'orderOverdueRate',
      align: 'center',
      width: 100,
      render: (value: number) => (
        <Text type={value > 10 ? 'danger' : value > 5 ? 'warning' : 'success'}>
          {value?.toFixed?.(2)}%
        </Text>
      )
    },
    {
      title: '已处理逾期订单数',
      key: 'fixOrdersInProgress',
      align: 'center',
      width: 130,
      render: (value: number, record: StoreStatistics) => <Text type='success'>{record.historicalOverdueOrderCount - record.overdueOrdersInProgress}</Text>
    },
    {
      title: '未处理逾期订单数',
      dataIndex: 'overdueOrdersInProgress',
      key: 'overdueOrdersInProgress',
      align: 'center',
      width: 130,
      render: (value: number) => <Text type={value > 0 ? 'danger' : 'success'}>{value}</Text>
    },
    {
      title: '应收总期数',
      dataIndex: 'receivableInstallmentPeriodsCount',
      key: 'receivableInstallmentPeriodsCount',
      align: 'center',
      width: 100,
    },
    {
      title: '总逾期期数',
      dataIndex: 'overduedInstallmentPeriodsCount',
      key: 'overduedInstallmentPeriodsCount',
      align: 'center',
      width: 100,
      render: (value: number) => <Text type={'warning'}>{value}</Text>
    },
    {
      title: '逾期率-期数',
      dataIndex: 'overdueRatePeriods',
      key: 'overdueRatePeriods',
      align: 'center',
      width: 100,
      render: (value: number) => (
        <Text type={value > 20 ? 'danger' : value > 10 ? 'warning' : 'success'}>
          {value?.toFixed?.(2)}%
        </Text>
      )
    },
    {
      title: '已处理逾期数',
      dataIndex: 'handledOverduedInstallmentPeriodsCount',
      key: 'handledOverduedInstallmentPeriodsCount',
      align: 'center',
      width: 120,
      render: (value: number) => <Text type={'success'}>{value}</Text>
    },
    {
      title: '逾期处理率',
      dataIndex: 'overdueProcessingRate',
      key: 'overdueProcessingRate',
      align: 'center',
      width: 100,
      render: (value: number) => (
        <Text type={value > 80 ? 'success' : value > 50 ? 'warning' : 'danger'}>
          {value?.toFixed?.(2)}%
        </Text>
      )
    },
    {
      title: '未处理逾期期数',
      dataIndex: 'overduingInstallmentPeriodsCount',
      key: 'overduingInstallmentPeriodsCount',
      align: 'center',
      width: 120,
      render: (value: number) => <Text type={value > 0 ? 'danger' : 'success'}>{value}</Text>
    },
    {
      title: '逾期总金额',
      dataIndex: 'overduedInstallmentPeriodsAmount',
      key: 'overduedInstallmentPeriodsAmount',
      align: 'center',
      width: 120,
      render: (value: number) => (
        <Text>¥{(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
      )
    },
    {
      title: '已处理逾期金额',
      dataIndex: 'handledOverduedInstallmentPeriodsAmount',
      key: 'handledOverduedInstallmentPeriodsAmount',
      align: 'center',
      width: 120,
      render: (value: number) => (
        <Text type={'success'}>¥{(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
      )
    },
    {
      title: '未处理逾期金额',
      dataIndex: 'overduingInstallmentPeriodsAmount',
      key: 'overduingInstallmentPeriodsAmount',
      align: 'center',
      width: 120,
      render: (value: number) => (
        <Text type={value > 0 ? 'danger' : 'success'}>¥{(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
      )
    },
    {
      title: '预估最大资损',
      dataIndex: 'estimatedMaxLoss',
      key: 'estimatedMaxLoss',
      align: 'center',
      width: 120,
      render: (value: number) => (
        <Text type={value > 0 ? 'danger' : 'success'}>¥{(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
      )
    },
    {
      title: '预估最大资损率',
      dataIndex: 'estimatedMaxLossRate',
      key: 'estimatedMaxLossRate',
      align: 'center',
      width: 120,
      render: (value: number) => (
        <Text type={value > 50 ? 'danger' : value > 20 ? 'warning' : 'success'}>
          {value?.toFixed?.(2)}%
        </Text>
      )
    }
  ];

  // 导出已整合到 DataTable 组件

  return (
    <Dashboard
      title={"逾期店铺统计"}
      queryFields={fields}
      fetcher={async (params: any) => {
        const [statisResp, stats] = await Promise.all([
          overdueApi.getStoreAggregateStatistics(params),
          overdueApi.getOverdueSummary(params)
        ]);
        return { rows: statisResp.statistics as any, stats: stats };
      }}
      defaultQuery={getDefaultQuery()}
      kpiItems={getKpiStatic}
      sections={[{
        key: 'store-stats-table',
        title: '逾期店铺数据概览',
        extra: (
          <Space>
            <Button icon={<FileExcelOutlined />} onClick={() => tableRef.current?.exportCSV({ filename: 'overdue-store-statistics.csv' })}>导出Excel</Button>
          </Space>
        ),
        render: ({ rows }) => (
          <DataTable
            columns={columns}
            dataSource={rows as any}
            rowKey={(r: any) => String(r.storeId)}
            groupHeaders={[
              {
                title: '订单量',
                children: [ 'm1OrderCount', 'historicalOverdueOrderCount', 'orderOverdueRate', 'fixOrdersInProgress', 'overdueOrdersInProgress', ]
              },
              {
                title: '期数',
                children: ['receivableInstallmentPeriodsCount', 'overduedInstallmentPeriodsCount', 'overdueRatePeriods', 'handledOverduedInstallmentPeriodsCount', 'overdueProcessingRate', 'overduingInstallmentPeriodsCount']
              },
            ]}
            ref={tableRef as any}
          />
        )
      }]}
    />
  );
};

export default OverdueStoreStatistics;
